<script>
import { dailyRecordsAPI } from '../../../api/dailyRecords';
import { attendanceTypesAPI } from '../../../api/attendanceTypes';
import { specialtiesAPI } from '../../../api/specialties';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

export default {
  name: 'RegistrosView',
  data() {
    return {
      records: [],
      professionals: [],
      patients: [],
      attendanceTypes: [],
      specialties: [],
      loading: true,
      // Client-side sorting of the grid; empty key keeps the backend order.
      sortKey: '',
      sortOrder: 'asc',
      filters: {
        personId: this.$route.query.personId || '',
        professionalId: '',
        startedAfter: '',
        startedBefore: '',
      },
      lockedPatientName: this.$route.query.personName || '',
      showModal: false,
      showViewModal: false,
      viewRecord: null,
      isSaving: false,
      // Guards the PDF export: on wide date ranges the backend takes a while and,
      // with no feedback, users click "Exportar" repeatedly.
      isExporting: false,
      isExportingClosing: false,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    isPatientLocked() {
      return !!this.$route.query.personId;
    },
    // Non-admin agents are matched to their own Professional record by e-mail —
    // there is no explicit chatwoot_agent_id link in the crm-ms today.
    linkedProfessional() {
      const email = (this.currentUser?.email || '').trim().toLowerCase();
      if (!email) return null;
      return this.professionals.find(
        p => (p.email || '').trim().toLowerCase() === email
      );
    },
    isEditing() {
      return !!this.form.id;
    },
    // Dropdown options (filters + modal) always shown A→Z, accent/case-insensitive.
    sortedPatients() {
      return [...this.patients].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
    },
    sortedProfessionals() {
      return [...this.professionals].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
    },
    // As listas vêm só com fichas ativas — oferecer uma inativa permitiria lançar
    // atendimento num cadastro desativado ou consolidado noutro. Mas ao EDITAR um
    // registro antigo cujo paciente foi inativado depois, a opção sumiria e o campo
    // apareceria vazio, como se o registro não tivesse paciente. Nesses casos a opção
    // volta, marcada como inativa, só para aquele registro.
    formPatientOptions() {
      return this.withInactiveSelected(
        this.sortedPatients,
        this.form.personId,
        this.form.patientNameSnapshot
      );
    },
    formProfessionalOptions() {
      return this.withInactiveSelected(
        this.sortedProfessionals,
        this.form.professionalId,
        this.form.professionalNameSnapshot
      );
    },
    // Especialidades oferecidas pela profissional escolhida. Quem não tem nenhuma
    // amarrada ainda cai na lista completa, senão o registro ficaria sem especialidade
    // (e sem valor no fechamento) sem o usuário entender por quê.
    formProfessionalSpecialties() {
      const professionalId = this.isAdmin
        ? this.form.professionalId
        : this.linkedProfessional?.id;
      const professional = this.professionals.find(
        p => String(p.id) === String(professionalId)
      );
      const ids = professional?.specialtyIds || [];
      if (!ids.length) return this.specialties;
      return this.specialties.filter(s => ids.some(id => String(id) === String(s.id)));
    },
    sortedRecords() {
      if (!this.sortKey) return this.records;
      const dir = this.sortOrder === 'asc' ? 1 : -1;
      // Copy before sorting — never mutate the fetched array in place.
      return [...this.records].sort((a, b) => {
        const va = this.sortValue(a, this.sortKey);
        const vb = this.sortValue(b, this.sortKey);
        // Dates are naive ISO strings (lexicographic == chronological); names use
        // locale-aware, accent/case-insensitive comparison. numeric handles both.
        return String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base', numeric: true }) * dir;
      });
    },
  },
  watch: {
    // Uma única especialidade não merece uma escolha: pré-seleciona. Com duas ou mais
    // (ex.: Psicomotricidade e Fisioterapia) a decisão é por atendimento e fica com o
    // usuário — chutar aqui cobraria o valor errado.
    // O watch só dispara quando a lista MUDA. Para a profissional que lança os próprios
    // atendimentos a lista nunca muda, então ele rodava uma vez ao carregar a página e
    // todo formulário novo (getEmptyForm) nascia vazio: só o 1º lançamento levava a
    // especialidade (CIES, out/2026: 128 de 129 sem valor no fechamento). Por isso o
    // preenchimento também roda ao abrir o modal, em preselectSingleSpecialty.
    formProfessionalSpecialties() {
      this.preselectSingleSpecialty();
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchProfessionals(),
      this.fetchPatients(),
      this.fetchAttendanceTypes(),
      this.fetchSpecialties(),
    ]);
    await this.fetchRecords();
  },
  methods: {
    withInactiveSelected(options, selectedId, fallbackName) {
      if (!selectedId) return options;
      if (options.some(o => String(o.id) === String(selectedId))) return options;
      return [
        ...options,
        {
          id: selectedId,
          name: `${fallbackName || `#${selectedId}`} ${this.$t('REGISTROS.FORM.INACTIVE_SUFFIX')}`,
        },
      ];
    },
    getEmptyForm() {
      return {
        id: null,
        personId: this.$route.query.personId || '',
        professionalId: this.isAdmin ? '' : this.linkedProfessional?.id || '',
        startedAt: '',
        endedTime: '',
        attendanceTypeId: '',
        specialtyId: '',
        activityDescription: '',
        activityObjective: '',
        notes: '',
        authorAgentId: null,
        authorName: null,
        patientNameSnapshot: '',
        professionalNameSnapshot: '',
      };
    },
    formatDateTime(value) {
      if (!value) return '-';
      // The crm-ms stores/returns naive wall-clock timestamps (no timezone).
      // Format the ISO string literally — never through `new Date`, which would
      // apply the browser timezone and shift the displayed hour (the +3h bug).
      const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
      if (!match) return value;
      const [, year, month, day, hour, minute] = match;
      return `${day}/${month}/${year}, ${hour}:${minute}`;
    },
    toDatetimeLocal(value) {
      if (!value) return '';
      // Backend value is already naive wall-clock (`YYYY-MM-DDTHH:mm:ss`); slice
      // it to what <input type="datetime-local"> expects, with no timezone math.
      return value.slice(0, 16);
    },
    canEdit(record) {
      return String(record.authorAgentId) === String(this.currentUser?.id);
    },
    sortBy(key) {
      // Same column toggles asc/desc; a new column starts ascending.
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    sortValue(record, key) {
      switch (key) {
        case 'patient':
          return record.patientName || this.patientName(record.personId) || '';
        case 'professional':
          return record.professionalName || '';
        case 'started':
          return record.startedAt || '';
        case 'ended':
          return record.endedAt || '';
        default:
          return '';
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'i-lucide-chevrons-up-down opacity-40';
      return this.sortOrder === 'asc' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down';
    },
    async fetchProfessionals() {
      try {
        const { data } = await dailyRecordsAPI.professionals(this.accountId);
        this.professionals = data || [];
      } catch {
        this.professionals = [];
      }
    },
    async fetchPatients() {
      try {
        const { data } = await dailyRecordsAPI.patients(this.accountId);
        this.patients = data || [];
      } catch {
        this.patients = [];
      }
    },
    async fetchSpecialties() {
      try {
        const { data } = await specialtiesAPI.getAll(this.accountId, true);
        this.specialties = data || [];
      } catch {
        this.specialties = [];
      }
    },
    async fetchAttendanceTypes() {
      try {
        const { data } = await attendanceTypesAPI.getAll(this.accountId, true);
        this.attendanceTypes = data || [];
      } catch {
        this.attendanceTypes = [];
      }
    },
    // The backend binds startedAfter/startedBefore as LocalDateTime (ISO.DATE_TIME,
    // requires a `T` + time-of-day). The <input type="date"> filters only ever give
    // us a bare "YYYY-MM-DD", which fails that bind — pad to start/end of day here.
    dateRangeParams() {
      const { startedAfter, startedBefore, ...rest } = this.filters;
      return {
        ...rest,
        startedAfter: startedAfter ? `${startedAfter}T00:00:00` : '',
        startedBefore: startedBefore ? `${startedBefore}T23:59:59` : '',
      };
    },
    async fetchRecords() {
      this.loading = true;
      try {
        // Non-admin agents only ever see their own records — enforced here (not
        // exposed as an editable filter) so it can't be cleared from the UI.
        const params = {
          ...this.dateRangeParams(),
          authorAgentId: this.isAdmin ? '' : this.currentUser?.id,
        };
        const { data } = await dailyRecordsAPI.search(this.accountId, params);
        this.records = data || [];
      } catch {
        useAlert(this.$t('REGISTROS.ERRORS.FETCH'));
      } finally {
        this.loading = false;
      }
    },
    async exportRecords() {
      // Double-click guard: the button is disabled while exporting, but a queued
      // click can still land before Vue re-renders.
      if (this.isExporting) return;
      this.isExporting = true;
      try {
        const params = {
          ...this.dateRangeParams(),
          authorAgentId: this.isAdmin ? '' : this.currentUser?.id,
        };
        const { data } = await dailyRecordsAPI.exportPdf(this.accountId, params);
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'registros-diarios.pdf';
        link.click();
        URL.revokeObjectURL(url);
      } catch {
        useAlert(this.$t('REGISTROS.ERRORS.EXPORT'));
      } finally {
        this.isExporting = false;
      }
    },
    async exportFinancialClosing() {
      if (this.isExportingClosing) return;
      this.isExportingClosing = true;
      try {
        // Sem authorAgentId: o fechamento é de administrador e cobre todos os
        // profissionais, não só os registros lançados por quem clicou.
        const { data } = await dailyRecordsAPI.exportFinancialClosing(
          this.accountId,
          this.dateRangeParams()
        );
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'fechamento-financeiro.pdf';
        link.click();
        URL.revokeObjectURL(url);
      } catch {
        useAlert(this.$t('REGISTROS.ERRORS.FINANCIAL_CLOSING'));
      } finally {
        this.isExportingClosing = false;
      }
    },
    clearFilters() {
      this.filters = {
        personId: this.$route.query.personId || '',
        professionalId: '',
        startedAfter: '',
        startedBefore: '',
      };
      this.fetchRecords();
    },
    patientName(personId) {
      const patient = this.patients.find(p => String(p.id) === String(personId));
      return patient ? patient.name : '';
    },
    openAddModal() {
      if (!this.isAdmin && !this.linkedProfessional) {
        useAlert(this.$t('REGISTROS.FORM.NO_LINKED_PROFESSIONAL'));
        return;
      }
      this.form = this.getEmptyForm();
      this.preselectSingleSpecialty();
      this.showModal = true;
    },
    preselectSingleSpecialty() {
      if (this.form.specialtyId) return;
      const options = this.formProfessionalSpecialties;
      if (options.length === 1) {
        this.form.specialtyId = options[0].id;
      }
    },
    openEditModal(record) {
      this.form = {
        id: record.id,
        personId: record.personId,
        professionalId: record.professionalId || '',
        startedAt: this.toDatetimeLocal(record.startedAt),
        endedTime: this.toDatetimeLocal(record.endedAt).slice(11, 16),
        attendanceTypeId: record.attendanceTypeId || '',
        specialtyId: record.specialtyId || '',
        activityDescription: record.activityDescription || '',
        activityObjective: record.activityObjective || '',
        notes: record.notes || '',
        // Preserve the original creator so the author is never overwritten on update.
        authorAgentId: record.authorAgentId,
        authorName: record.authorName,
        // Nomes gravados no próprio registro: se a ficha tiver sido inativada depois,
        // são a única forma de ainda mostrar de quem era o atendimento.
        patientNameSnapshot: record.patientName || '',
        professionalNameSnapshot: record.professionalName || '',
      };
      // Lançamento antigo que ficou sem especialidade ganha a única possível ao ser editado.
      this.preselectSingleSpecialty();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.form = this.getEmptyForm();
    },
    openViewModal(record) {
      this.viewRecord = record;
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
      this.viewRecord = null;
    },
    validateForm() {
      if (!this.form.personId) {
        useAlert(this.$t('REGISTROS.FORM.PATIENT_REQUIRED'));
        return false;
      }
      if (!this.form.startedAt || !this.form.endedTime) {
        useAlert(this.$t('REGISTROS.FORM.DATES_REQUIRED'));
        return false;
      }
      // End date is always the same as the start date — only the end time is
      // entered — so compare just the `HH:mm` portions, no timezone-sensitive Date.
      if (this.form.endedTime <= this.form.startedAt.slice(11, 16)) {
        useAlert(this.$t('REGISTROS.FORM.END_AFTER_START'));
        return false;
      }
      if (!this.form.attendanceTypeId) {
        useAlert(this.$t('REGISTROS.FORM.ATTENDANCE_TYPE_REQUIRED'));
        return false;
      }
      if (!this.form.activityDescription.trim() || !this.form.activityObjective.trim()) {
        useAlert(this.$t('REGISTROS.FORM.DESCRIPTION_REQUIRED'));
        return false;
      }
      return true;
    },
    async saveRecord() {
      if (!this.validateForm()) return;
      this.isSaving = true;
      // Non-admin agents can never pick a different professional than the one
      // linked to their own user — never trust the form value alone for them.
      const professionalId = this.isAdmin
        ? this.form.professionalId
        : this.linkedProfessional?.id;
      const professional = this.professionals.find(
        p => String(p.id) === String(professionalId)
      );
      const attendanceType = this.attendanceTypes.find(
        t => String(t.id) === String(this.form.attendanceTypeId)
      );
      const specialty = this.specialties.find(
        s => String(s.id) === String(this.form.specialtyId)
      );
      const payload = {
        personId: Number(this.form.personId),
        patientName: this.lockedPatientName || this.patientName(this.form.personId),
        professionalId: professionalId ? Number(professionalId) : null,
        professionalName: professional?.name || null,
        attendanceTypeId: Number(this.form.attendanceTypeId),
        attendanceTypeDescription: attendanceType?.description || null,
        // Denormalised like professionalName/attendanceTypeDescription: the report keeps
        // the label even if the specialty is renamed or deactivated later.
        specialtyId: this.form.specialtyId ? Number(this.form.specialtyId) : null,
        specialtyName: specialty?.name || null,
        // Send the naive wall-clock the user typed, exactly as-is. Do NOT convert
        // to UTC (`toISOString`): the crm-ms stores `LocalDateTime` as received
        // and drops any `Z`, so a UTC conversion here shifts the value by +3h.
        startedAt: `${this.form.startedAt.slice(0, 16)}:00`,
        // End date is always the same calendar day as the start — only the time
        // is user-entered.
        endedAt: `${this.form.startedAt.slice(0, 10)}T${this.form.endedTime}:00`,
        activityDescription: this.form.activityDescription,
        activityObjective: this.form.activityObjective,
        notes: this.form.notes,
        // On edit, keep the original creator; on create, stamp the current user.
        authorAgentId: this.isEditing ? this.form.authorAgentId : this.currentUser?.id,
        authorName: this.isEditing ? this.form.authorName : this.currentUser?.name,
      };
      try {
        if (this.isEditing) {
          await dailyRecordsAPI.update(this.accountId, this.form.id, payload);
          useAlert(this.$t('REGISTROS.UPDATED'));
        } else {
          await dailyRecordsAPI.create(this.accountId, payload);
          useAlert(this.$t('REGISTROS.SAVED'));
        }
        this.closeModal();
        await this.fetchRecords();
      } catch {
        useAlert(
          this.$t(this.isEditing ? 'REGISTROS.ERRORS.UPDATE' : 'REGISTROS.ERRORS.SAVE')
        );
      } finally {
        this.isSaving = false;
      }
    },
    async deleteRecord(id) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('REGISTROS.DELETE_CONFIRM'))) return;
      try {
        await dailyRecordsAPI.delete(this.accountId, id);
        useAlert(this.$t('REGISTROS.DELETED'));
        await this.fetchRecords();
      } catch {
        useAlert(this.$t('REGISTROS.ERRORS.DELETE'));
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full px-6 py-6 overflow-y-auto bg-slate-50">
    <div class="flex items-center justify-between pb-6">
      <h1 class="text-2xl font-bold text-slate-800">
        {{ $t('REGISTROS.TITLE') }}
        <span v-if="lockedPatientName" class="text-slate-500">— {{ lockedPatientName }}</span>
      </h1>
      <div class="flex gap-2">
        <button
          :disabled="isExporting"
          class="flex items-center gap-2 px-4 py-2 font-medium transition-colors border rounded-md shadow-sm text-slate-700 bg-white border-slate-300 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white"
          @click="exportRecords"
        >
          <span
            v-if="isExporting"
            class="inline-block w-4 h-4 border-2 rounded-full border-slate-300 border-t-slate-600 animate-spin"
          />
          {{ isExporting ? $t('REGISTROS.EXPORTING') : $t('REGISTROS.EXPORT_BUTTON') }}
        </button>
        <button
          v-if="isAdmin"
          :disabled="isExportingClosing"
          class="flex items-center gap-2 px-4 py-2 font-medium transition-colors border rounded-md shadow-sm text-slate-700 bg-white border-slate-300 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white"
          @click="exportFinancialClosing"
        >
          <span
            v-if="isExportingClosing"
            class="inline-block w-4 h-4 border-2 rounded-full border-slate-300 border-t-slate-600 animate-spin"
          />
          {{ isExportingClosing ? $t('REGISTROS.EXPORTING') : $t('REGISTROS.FINANCIAL_CLOSING_BUTTON') }}
        </button>
        <button
          class="px-4 py-2 font-medium text-white transition-colors rounded-md shadow-sm bg-fluxi-primary hover:bg-fluxi-primary/90"
          @click="openAddModal"
        >
          {{ $t('REGISTROS.ADD_BUTTON') }}
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="flex flex-wrap items-end gap-4 p-5 mb-6 bg-white border rounded-lg shadow-sm border-slate-200">
      <div v-if="!isPatientLocked" class="flex flex-col">
        <label class="mb-1 text-xs font-semibold text-slate-600">{{ $t('REGISTROS.FILTERS.PATIENT') }}</label>
        <select
          v-model="filters.personId"
          class="w-48 px-3 py-2 text-sm bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('REGISTROS.FILTERS.ALL') }}</option>
          <option v-for="p in sortedPatients" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>
      <div class="flex flex-col">
        <label class="mb-1 text-xs font-semibold text-slate-600">{{ $t('REGISTROS.FILTERS.PROFESSIONAL') }}</label>
        <select
          v-model="filters.professionalId"
          class="w-48 px-3 py-2 text-sm bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('REGISTROS.FILTERS.ALL') }}</option>
          <option v-for="pro in sortedProfessionals" :key="pro.id" :value="pro.id">{{ pro.name }}</option>
        </select>
      </div>
      <div class="flex flex-col">
        <label class="mb-1 text-xs font-semibold text-slate-600">{{ $t('REGISTROS.FILTERS.FROM') }}</label>
        <input
          v-model="filters.startedAfter"
          type="date"
          class="px-3 py-2 text-sm border rounded outline-none border-slate-300 focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex flex-col">
        <label class="mb-1 text-xs font-semibold text-slate-600">{{ $t('REGISTROS.FILTERS.TO') }}</label>
        <input
          v-model="filters.startedBefore"
          type="date"
          class="px-3 py-2 text-sm border rounded outline-none border-slate-300 focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex gap-2 ml-auto">
        <button
          class="px-6 py-2 text-sm font-medium text-white transition-colors rounded shadow-sm bg-fluxi-primary hover:bg-fluxi-primary/90"
          @click="fetchRecords"
        >
          {{ $t('REGISTROS.FILTERS.FILTER_BUTTON') }}
        </button>
        <button
          class="px-6 py-2 text-sm font-medium border rounded shadow-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
          @click="clearFilters"
        >
          {{ $t('REGISTROS.FILTERS.CLEAR_BUTTON') }}
        </button>
      </div>
    </div>

    <!-- Tabela -->
    <div class="overflow-x-auto bg-white border rounded-lg shadow-sm border-slate-200">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-slate-50/50">
          <tr>
            <th
              class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase transition-colors cursor-pointer select-none text-slate-500 hover:text-slate-700"
              @click="sortBy('patient')"
            >
              <span class="inline-flex items-center gap-1">
                {{ $t('REGISTROS.TABLE.PATIENT') }}
                <span :class="sortIcon('patient')" class="size-3.5" />
              </span>
            </th>
            <th
              class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase transition-colors cursor-pointer select-none text-slate-500 hover:text-slate-700"
              @click="sortBy('professional')"
            >
              <span class="inline-flex items-center gap-1">
                {{ $t('REGISTROS.TABLE.PROFESSIONAL') }}
                <span :class="sortIcon('professional')" class="size-3.5" />
              </span>
            </th>
            <th
              class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase transition-colors cursor-pointer select-none text-slate-500 hover:text-slate-700"
              @click="sortBy('started')"
            >
              <span class="inline-flex items-center gap-1">
                {{ $t('REGISTROS.TABLE.START') }}
                <span :class="sortIcon('started')" class="size-3.5" />
              </span>
            </th>
            <th
              class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase transition-colors cursor-pointer select-none text-slate-500 hover:text-slate-700"
              @click="sortBy('ended')"
            >
              <span class="inline-flex items-center gap-1">
                {{ $t('REGISTROS.TABLE.END') }}
                <span :class="sortIcon('ended')" class="size-3.5" />
              </span>
            </th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">{{ $t('REGISTROS.TABLE.DESCRIPTION') }}</th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-center uppercase text-slate-500">{{ $t('REGISTROS.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-if="loading">
            <td colspan="6" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('REGISTROS.TABLE.LOADING') }}</td>
          </tr>
          <tr v-else-if="records.length === 0">
            <td colspan="6" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('REGISTROS.TABLE.EMPTY') }}</td>
          </tr>
          <template v-else>
            <tr v-for="record in sortedRecords" :key="record.id" class="transition-colors hover:bg-slate-50/50">
              <td class="px-6 py-4 text-sm font-medium text-slate-700">{{ record.patientName || patientName(record.personId) || '-' }}</td>
              <td class="px-6 py-4 text-sm text-slate-600">{{ record.professionalName || '-' }}</td>
              <td class="px-6 py-4 text-sm whitespace-nowrap text-slate-600">{{ formatDateTime(record.startedAt) }}</td>
              <td class="px-6 py-4 text-sm whitespace-nowrap text-slate-600">{{ formatDateTime(record.endedAt) }}</td>
              <td class="px-6 py-4 text-sm text-slate-600">{{ record.activityDescription }}</td>
              <td class="px-6 py-4 space-x-2 text-sm font-medium text-center whitespace-nowrap">
                <button
                  class="inline-flex items-center px-3 py-1 text-xs font-medium transition-colors rounded text-fluxi-primary bg-fluxi-primary/10 hover:bg-fluxi-primary/20"
                  @click="openViewModal(record)"
                >
                  {{ $t('REGISTROS.TABLE.VIEW') }}
                </button>
                <button
                  v-if="canEdit(record)"
                  class="inline-flex items-center px-3 py-1 text-xs font-medium text-amber-700 transition-colors rounded bg-amber-50 hover:bg-amber-100"
                  @click="openEditModal(record)"
                >
                  {{ $t('REGISTROS.TABLE.EDIT') }}
                </button>
                <button
                  v-if="canEdit(record) || isAdmin"
                  class="inline-flex items-center px-3 py-1 text-xs font-medium text-red-600 transition-colors rounded bg-red-50 hover:bg-red-100"
                  @click="deleteRecord(record.id)"
                >
                  {{ $t('REGISTROS.TABLE.DELETE') }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Modal Adicionar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 class="text-lg font-bold text-slate-800">{{ isEditing ? $t('REGISTROS.MODAL.EDIT_TITLE') : $t('REGISTROS.MODAL.ADD_TITLE') }}</h2>
          <button class="text-xl font-bold text-slate-400 hover:text-slate-600" @click="closeModal">
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-grow px-6 py-4 overflow-y-auto">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.PATIENT') }}</label>
              <input
                v-if="isPatientLocked"
                :value="lockedPatientName"
                type="text"
                disabled
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 bg-slate-100"
              />
              <select
                v-else
                v-model="form.personId"
                class="w-full px-3 py-2 bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              >
                <option value="">{{ $t('REGISTROS.FORM.SELECT_PATIENT') }}</option>
                <option v-for="p in formPatientOptions" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>

            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.PROFESSIONAL') }}</label>
              <input
                v-if="!isAdmin"
                :value="linkedProfessional?.name || ''"
                type="text"
                disabled
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 bg-slate-100"
              />
              <select
                v-else
                v-model="form.professionalId"
                class="w-full px-3 py-2 bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              >
                <option value="">{{ $t('REGISTROS.FORM.SELECT_PROFESSIONAL') }}</option>
                <option v-for="pro in formProfessionalOptions" :key="pro.id" :value="pro.id">{{ pro.name }}</option>
              </select>
            </div>

            <div>
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.STARTED_AT') }}</label>
              <input
                v-model="form.startedAt"
                type="datetime-local"
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.ENDED_AT') }}</label>
              <input
                v-model="form.endedTime"
                type="time"
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>

            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.ATTENDANCE_TYPE') }}</label>
              <select
                v-model="form.attendanceTypeId"
                class="w-full px-3 py-2 bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              >
                <option value="">{{ $t('REGISTROS.FORM.SELECT_ATTENDANCE_TYPE') }}</option>
                <option v-for="t in attendanceTypes" :key="t.id" :value="t.id">{{ t.description }}</option>
              </select>
            </div>

            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.SPECIALTY') }}</label>
              <select
                v-model="form.specialtyId"
                class="w-full px-3 py-2 bg-white border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              >
                <option value="">{{ $t('REGISTROS.FORM.SELECT_SPECIALTY') }}</option>
                <option v-for="s in formProfessionalSpecialties" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
              <p class="mt-1 text-xs text-slate-500">{{ $t('REGISTROS.FORM.SPECIALTY_HINT') }}</p>
            </div>

            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.ACTIVITY_DESCRIPTION') }}</label>
              <textarea
                v-model="form.activityDescription"
                rows="2"
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>
            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.ACTIVITY_OBJECTIVE') }}</label>
              <textarea
                v-model="form.activityObjective"
                rows="2"
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>
            <div class="col-span-2">
              <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('REGISTROS.FORM.NOTES') }}</label>
              <textarea
                v-model="form.notes"
                rows="2"
                class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            class="px-4 py-2 font-medium bg-white border rounded text-slate-600 border-slate-300 hover:bg-slate-50"
            @click="closeModal"
          >
            {{ $t('REGISTROS.MODAL.CANCEL') }}
          </button>
          <button
            :disabled="isSaving"
            class="px-6 py-2 font-medium text-white rounded bg-fluxi-primary hover:bg-fluxi-primary/90 disabled:opacity-50"
            @click="saveRecord"
          >
            {{ isSaving ? $t('REGISTROS.MODAL.SAVING') : $t('REGISTROS.MODAL.SAVE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Visualizar -->
    <div
      v-if="showViewModal && viewRecord"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 class="text-lg font-bold text-slate-800">{{ $t('REGISTROS.VIEW.TITLE') }}</h2>
          <button class="text-xl font-bold text-slate-400 hover:text-slate-600" @click="closeViewModal">
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-grow px-6 py-5 space-y-4 overflow-y-auto">
          <p class="text-sm text-slate-700">
            <span class="font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.PATIENT') }}:</span>
            {{ viewRecord.patientName || patientName(viewRecord.personId) || '-' }}
          </p>
          <p class="text-sm text-slate-700">
            <span class="font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.PROFESSIONAL') }}:</span>
            {{ viewRecord.professionalName || '-' }}
          </p>
          <p class="text-sm text-slate-700">
            <span class="font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.START') }}:</span>
            {{ formatDateTime(viewRecord.startedAt) }}
          </p>
          <p class="text-sm text-slate-700">
            <span class="font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.END') }}:</span>
            {{ formatDateTime(viewRecord.endedAt) }}
          </p>
          <p class="text-sm text-slate-700">
            <span class="font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.ATTENDANCE_TYPE') }}:</span>
            {{ viewRecord.attendanceTypeDescription || '-' }}
          </p>

          <div>
            <p class="mb-1 text-sm font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.DESCRIPTION') }}:</p>
            <p class="px-4 py-3 text-sm whitespace-pre-line rounded border-l-4 text-slate-700 border-fluxi-primary bg-slate-50">
              {{ viewRecord.activityDescription || '-' }}
            </p>
          </div>
          <div>
            <p class="mb-1 text-sm font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.OBJECTIVE') }}:</p>
            <p class="px-4 py-3 text-sm whitespace-pre-line rounded border-l-4 text-slate-700 border-fluxi-primary bg-slate-50">
              {{ viewRecord.activityObjective || '-' }}
            </p>
          </div>
          <div v-if="viewRecord.notes">
            <p class="mb-1 text-sm font-semibold text-slate-800">{{ $t('REGISTROS.VIEW.NOTES') }}:</p>
            <p class="px-4 py-3 text-sm whitespace-pre-line rounded border-l-4 text-slate-700 border-fluxi-primary bg-slate-50">
              {{ viewRecord.notes }}
            </p>
          </div>

          <p v-if="viewRecord.createdAt" class="pt-2 text-xs italic text-slate-400">
            {{ $t('REGISTROS.VIEW.CREATED_BY', { date: formatDateTime(viewRecord.createdAt), author: viewRecord.authorName || '-' }) }}
          </p>
        </div>

        <div class="flex justify-end px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            class="px-4 py-2 font-medium bg-white border rounded text-slate-600 border-slate-300 hover:bg-slate-50"
            @click="closeViewModal"
          >
            {{ $t('REGISTROS.VIEW.CLOSE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Overlay do export: bloqueia a tela enquanto o PDF é gerado, para o
         usuário não achar que nada aconteceu e clicar em "Exportar" de novo. -->
    <div
      v-if="isExporting || isExportingClosing"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
    >
      <div
        class="flex flex-col items-center gap-3 px-8 py-6 bg-white rounded-lg shadow-lg"
        role="status"
        aria-live="polite"
      >
        <span
          class="inline-block w-8 h-8 border-4 rounded-full border-slate-200 border-t-fluxi-primary animate-spin"
        />
        <p class="text-sm font-medium text-slate-700">{{ $t('REGISTROS.EXPORTING') }}</p>
        <p class="text-xs text-slate-500">{{ $t('REGISTROS.EXPORTING_HINT') }}</p>
      </div>
    </div>
  </div>
</template>
